import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import Reveal, { fadeUp } from "@/components/motion/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
  captcha_answer: "",
};

export default function Contact() {
  const { t } = useI18n();
  const c = t.contact;
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [captcha, setCaptcha] = useState(() => ({
    a: Math.floor(Math.random() * 9) + 1,
    b: Math.floor(Math.random() * 9) + 1,
  }));

  // Pre-fill subject/message from URL params (?subject=...&message=...) for deep-linked CTAs
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const qSubject = params.get("subject");
    const qMessage = params.get("message");
    if (!qSubject && !qMessage) return;
    setForm((f) => ({
      ...f,
      subject: qSubject || f.subject,
      message: qMessage || f.message,
    }));
    // Scroll to contact section so the user sees the prefilled form
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    // Clean the URL so subsequent edits aren't overwritten on re-renders
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("subject");
      url.searchParams.delete("message");
      window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + (url.hash || ""));
    } catch (_) { /* noop */ }
  }, []);

  const newCaptcha = () => {
    setCaptcha({ a: Math.floor(Math.random() * 9) + 1, b: Math.floor(Math.random() * 9) + 1 });
    setForm((f) => ({ ...f, captcha_answer: "" }));
  };

  const captchaQuestion = useMemo(() => `${captcha.a} + ${captcha.b} = ?`, [captcha]);

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = c.errors.name;
    if (form.company.trim().length < 1) e.company = c.errors.company;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = c.errors.email;
    const digits = (form.phone.match(/\d/g) || []).length;
    if (digits < 5) e.phone = c.errors.phone;
    if (form.subject.trim().length < 2) e.subject = c.errors.subject;
    if (form.message.trim().length < 10) e.message = c.errors.message;
    if (form.captcha_answer === "" || isNaN(Number(form.captcha_answer))) {
      e.captcha_answer = c.errors.captchaEmpty;
    } else if (Number(form.captcha_answer) !== captcha.a + captcha.b) {
      e.captcha_answer = c.errors.captchaWrong;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => {
    setForm((f) => ({ ...f, [k]: ev.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(c.errors.toastValidation);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        captcha_a: captcha.a,
        captcha_b: captcha.b,
        captcha_answer: Number(form.captcha_answer),
      };
      const res = await axios.post(`${API}/contact`, payload);
      setSuccess({ reference: res.data.reference, message: res.data.message });
      setForm(initialForm);
      newCaptcha();
      toast.success(c.toastSuccess);
    } catch (err) {
      const detail = err?.response?.data?.detail || c.errors.toastNetwork;
      if (typeof detail === "string") toast.error(detail);
      else toast.error(c.errors.toastNetwork);
      if (err?.response?.status === 400 && typeof detail === "string" && detail.toLowerCase().includes("captcha")) {
        setErrors((e) => ({ ...e, captcha_answer: detail }));
        newCaptcha();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-pad bg-white relative">
      <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
      <div className="container-pro relative grid md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-5">
          <div className="overline">{c.kicker}</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">{c.title}</h2>
          <p className="mt-5 text-ink-secondary text-base md:text-lg leading-relaxed">{c.body}</p>

          <div className="mt-10 space-y-5 border-t border-line pt-8">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-surface-muted text-brand-red border border-line">
                <Mail size={18} />
              </span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{c.emailLabel}</div>
                <a href="mailto:info@smartterminals.co" className="font-heading text-lg font-medium hover:text-brand-red transition-colors" data-testid="contact-email">
                  info@smartterminals.co
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-surface-muted text-brand-red border border-line">
                <span className="font-mono text-xs">OM</span>
              </span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{c.hqLabel}</div>
                <div className="font-heading text-lg font-medium">{c.hqValue}</div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal variants={fadeUp} className="md:col-span-7">
          {success ? (
            <SuccessPanel reference={success.reference} message={success.message} onReset={() => setSuccess(null)} c={c} />
          ) : (
            <form onSubmit={onSubmit} data-testid="contact-form" className="bg-surface-muted border border-line rounded-xl p-6 md:p-10" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field id="name" label={c.labels.name} testid="contact-input-name" error={errors.name}>
                  <input id="name" data-testid="contact-input-name" type="text" value={form.name} onChange={onChange("name")} className={inputCls(errors.name)} autoComplete="name" />
                </Field>
                <Field id="company" label={c.labels.company} testid="contact-input-company" error={errors.company}>
                  <input id="company" data-testid="contact-input-company" type="text" value={form.company} onChange={onChange("company")} className={inputCls(errors.company)} autoComplete="organization" />
                </Field>
                <Field id="email" label={c.labels.email} testid="contact-input-email" error={errors.email}>
                  <input id="email" data-testid="contact-input-email" type="email" value={form.email} onChange={onChange("email")} className={inputCls(errors.email)} autoComplete="email" />
                </Field>
                <Field id="phone" label={c.labels.phone} testid="contact-input-phone" error={errors.phone}>
                  <input id="phone" data-testid="contact-input-phone" type="tel" value={form.phone} onChange={onChange("phone")} className={inputCls(errors.phone)} autoComplete="tel" placeholder={c.placeholders.phone} />
                </Field>
                <Field id="subject" label={c.labels.subject} testid="contact-input-subject" error={errors.subject} className="sm:col-span-2">
                  <select id="subject" data-testid="contact-input-subject" value={form.subject} onChange={onChange("subject")} className={inputCls(errors.subject)}>
                    <option value="">{c.placeholders.subjectDefault}</option>
                    {c.subjects.map((s) => (<option key={s} value={s}>{s}</option>))}
                    {form.subject && !c.subjects.includes(form.subject) && (
                      <option value={form.subject}>{form.subject}</option>
                    )}
                  </select>
                </Field>
                <Field id="message" label={c.labels.message} testid="contact-input-message" error={errors.message} className="sm:col-span-2">
                  <textarea id="message" data-testid="contact-input-message" rows={5} value={form.message} onChange={onChange("message")} className={inputCls(errors.message) + " resize-y"} placeholder={c.placeholders.message} />
                </Field>

                <div className="sm:col-span-2 bg-white border border-line rounded-md p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{c.labels.captchaLabel}</div>
                    <div className="mt-1 font-heading text-base">
                      <span dir="ltr" className="inline-block">{captchaQuestion}</span>
                      <button type="button" onClick={newCaptcha} className="ms-3 text-xs text-ink-muted hover:text-brand-red underline-offset-2 hover:underline" data-testid="captcha-refresh">
                        {c.labels.captchaRefresh}
                      </button>
                    </div>
                  </div>
                  <div className="w-32">
                    <input type="number" data-testid="contact-input-captcha" value={form.captcha_answer} onChange={onChange("captcha_answer")} className={inputCls(errors.captcha_answer)} aria-label="Captcha answer" />
                  </div>
                </div>
                {errors.captcha_answer && (
                  <div className="sm:col-span-2 -mt-3 text-xs text-brand-red" data-testid="captcha-error">{errors.captcha_answer}</div>
                )}
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-ink-muted max-w-sm">{c.disclaimer}</p>
                <button type="submit" disabled={submitting} data-testid="contact-submit-button" className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin" /> {c.submitting}</>
                  ) : (
                    <>{c.submit} <ArrowRight size={16} data-mirror-rtl="true" /></>
                  )}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

const inputCls = (hasError) =>
  `w-full bg-white border ${hasError ? "border-brand-red" : "border-line"} rounded-md px-3.5 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors`;

const Field = ({ id, label, children, error, className = "", testid }) => (
  <div className={className}>
    <label htmlFor={id} className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-secondary block mb-1.5">{label}</label>
    {children}
    {error && (
      <div className="mt-1.5 text-xs text-brand-red" data-testid={`${testid}-error`}>{error}</div>
    )}
  </div>
);

const SuccessPanel = ({ reference, message, onReset, c }) => (
  <div data-testid="contact-success" className="bg-surface-dark text-white border border-line-dark rounded-xl p-8 md:p-12 relative overflow-hidden">
    <div className="absolute top-0 right-0 h-20 w-20 ticker opacity-40" aria-hidden />
    <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-brand-red text-white">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
    </div>
    <h3 className="mt-6 font-heading text-2xl md:text-3xl font-medium leading-tight">{c.success.title}</h3>
    <p className="mt-3 text-white/70 text-sm leading-relaxed">{message}</p>
    <div className="mt-6 inline-flex items-center gap-2 text-xs font-mono text-white/60 border border-white/20 rounded px-3 py-1.5" data-testid="contact-reference">
      {c.success.refLabel} · {reference}
    </div>
    <div className="mt-8">
      <button type="button" onClick={onReset} className="text-sm font-medium text-white border-b border-brand-red pb-1 hover:text-brand-red transition-colors" data-testid="contact-success-reset">
        {c.success.reset}
      </button>
    </div>
  </div>
);
